
import React from 'react';
import { Card } from "@/components/ui/card";
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import PerformanceDashboard from './PerformanceDashboard';
import { AdaptiveDifficulty } from './AdaptiveDifficulty';
import { BenchmarkAnalysis } from './BenchmarkAnalysis';
import { Loader2 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import type { PerformanceData } from './types';
import { PerformanceTrendChart } from './PerformanceTrendChart';
import { QuizFunnelAnalysis } from './QuizFunnelAnalysis';
import { PredictiveAnalysis } from './PredictiveAnalysis';
import { RecommendationSystem } from './RecommendationSystem';

const Analytics = () => {
  const { isMobile } = useIsMobile();
  const { data: performanceData, isLoading } = useQuery({
    queryKey: ['performance_history'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data, error } = await supabase
        .from('performance_history')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as PerformanceData[];
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={`w-full ${isMobile ? 'space-y-4' : 'space-y-6'}`}>
      <PerformanceDashboard />
      
      {performanceData && performanceData.length > 0 && (
        <>
          <div className={`w-full ${isMobile ? 'px-0' : 'px-4'}`}>
            <PerformanceTrendChart performanceData={performanceData} />
          </div>
          
          <div className={`grid grid-cols-1 ${isMobile ? 'gap-4' : 'md:grid-cols-2 gap-6'}`}>
            <QuizFunnelAnalysis performanceData={performanceData} />
            <PredictiveAnalysis />
          </div>
          
          <div className={`w-full ${isMobile ? 'px-0' : 'px-4'}`}>
            <RecommendationSystem performanceData={performanceData} />
          </div>
          
          <AdaptiveDifficulty performanceData={performanceData} />
          <BenchmarkAnalysis />
        </>
      )}
    </div>
  );
};

export default Analytics;
